import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Lock, Mail, ArrowRight, User, Building2, KeyRound,
  ShieldCheck, UserCheck, BadgeCheck, Layers, CreditCard, LineChart,
  Map, Boxes, Search, Radio,
} from 'lucide-react';
import { useAuth, type UserRole } from '@/contexts/AuthContext';
import { authApi, AUTH_DEMO_ENABLED, AUTH_APP_ENV, type DemoAccount } from '@/lib/authApi';
import { GiisdecMark } from '@/components/portal/BrandLogo';

// WP2.1B-R2: presentation migrated to the LOCKED Public Portal split-screen
// authentication composition (giisdecsite_30 · invlogin / signup). ALL
// authentication logic — sign-in, register, demo-account helper, session-expiry
// notice, portal accent differentiation (investor emerald / GIS gold),
// navigation, and error handling — is preserved unchanged from the frozen
// baseline. Only presentation changed.

interface Props {
  title: string;
  subtitle: string;
  accent: 'emerald' | 'amber';
  role: UserRole;
  successPath: string;
  otherPortal: { label: string; to: string };
}

// ── Locked left-panel brand content, derived per portal + mode ───────────────
// Presentation-only. No data is fabricated; these are the approved marketing
// strings from the locked design's authentication views.
type PanelCard = { icon: React.ReactNode; label: string };

interface PanelContent {
  identity: string;
  badge: string;
  headLead: string;
  headAccent: string;
  blurb: string;
  cards: PanelCard[];
}

const isInvestor = (role: UserRole) => role === 'investor';

function leftPanel(role: UserRole, mode: 'signin' | 'signup'): PanelContent {
  if (isInvestor(role)) {
    if (mode === 'signup') {
      return {
        identity: 'Investor Onboarding',
        badge: 'Investor Onboarding · 2026',
        headLead: "Join Ghana's official",
        headAccent: 'mineral investment platform.',
        blurb:
          'Register as an investor, complete KYC verification, and access verified opportunities, permitted GIS layers and invoice payments verified by GIISDEC Finance. Access is provisional until verified.',
        cards: [
          { icon: <BadgeCheck className="w-4 h-4" />, label: 'Verified opportunities' },
          { icon: <Layers className="w-4 h-4" />, label: 'Entitlement-based GIS access' },
          { icon: <CreditCard className="w-4 h-4" />, label: 'Finance-verified payments' },
          { icon: <LineChart className="w-4 h-4" />, label: 'Real-time tracking' },
        ],
      };
    }
    return {
      identity: 'Investor Portal',
      badge: 'Investor Access Portal',
      headLead: "Invest in Ghana's",
      headAccent: 'mineral economy.',
      blurb:
        'Verified investor access to opportunities, KYC onboarding, agreements, Finance-verified payments and project intelligence.',
      cards: [
        { icon: <BadgeCheck className="w-4 h-4" />, label: 'Verified Opportunities' },
        { icon: <UserCheck className="w-4 h-4" />, label: 'KYC & Onboarding' },
        { icon: <CreditCard className="w-4 h-4" />, label: 'Agreements & Payments' },
        { icon: <LineChart className="w-4 h-4" />, label: 'Application Tracking' },
      ],
    };
  }
  // GIS analyst portal — same locked composition, GIS-appropriate content.
  return {
    identity: 'GIS Workspace',
    badge: 'GIS & Spatial Intelligence',
    headLead: "Explore Ghana's",
    headAccent: 'geospatial intelligence.',
    blurb:
      'Authorized access to concession layers, terrain models, satellite-backed analytics and spatial query tools for approved GIS users.',
    cards: [
      { icon: <Map className="w-4 h-4" />, label: 'Concession Layers' },
      { icon: <Boxes className="w-4 h-4" />, label: 'Terrain Models' },
      { icon: <Search className="w-4 h-4" />, label: 'Spatial Query' },
      { icon: <Radio className="w-4 h-4" />, label: 'Field Capture' },
    ],
  };
}

const LoginForm: React.FC<Props> = ({ title, subtitle, accent, role, successPath, otherPortal }) => {
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [organization, setOrganization] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [demoAccounts, setDemoAccounts] = useState<DemoAccount[] | null>(null);
  const [demoLoaded, setDemoLoaded] = useState(false);

  // EDS accent mapping — preserves the emerald (investor) vs gold (GIS) differentiation.
  const accentBtn = accent === 'emerald'
    ? 'bg-eds-primary hover:bg-eds-emerald-2 text-eds-primary-fg'
    : 'bg-eds-gold hover:bg-eds-gold-2 text-eds-warning-fg';
  // Keyboard focus is identical in both portal variants (P9G-FIX3): solid EDS focus colour, >= 3:1 on
  // light surfaces. Branding stays in the button, badge and headline.
  const focusRing = 'focus:ring-eds-ring focus:border-eds-ring';
  const accentText = accent === 'emerald' ? 'text-eds-emerald-2' : 'text-eds-earth';
  const accentChipBg = accent === 'emerald' ? 'bg-eds-success/12 border-eds-success/30' : 'bg-eds-gold/15 border-eds-gold/30';

  const inputCls =
    `w-full pl-10 pr-3 py-2.5 border border-eds-border rounded-eds-md text-sm bg-eds-surface-2 text-eds-fg placeholder:text-eds-muted-fg focus:outline-none focus:ring-2 ${focusRing}`;

  const panel = leftPanel(role, mode);
  const investor = isInvestor(role);

  // Surface a friendly notice if the user was kicked here by ProtectedRoute
  // after an expired session.
  useEffect(() => {
    const reason = (location.state as { reason?: string } | null)?.reason;
    if (reason === 'unauthenticated') {
      setError('Your session has expired. Please sign in again.');
    }
  }, [location.state]);

  // Lazily fetch demo accounts from geo-api in non-production builds.
  useEffect(() => {
    if (!AUTH_DEMO_ENABLED || demoLoaded) return;
    let cancelled = false;
    (async () => {
      const res = await authApi.listDemoAccounts();
      if (cancelled) return;
      setDemoAccounts(res.data?.accounts ?? []);
      setDemoLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [demoLoaded]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) return;
    if (mode === 'signup' && !consent) {
      setError('Please confirm the identity verification (KYC) consent to continue.');
      return;
    }
    setLoading(true);

    const result =
      mode === 'signin'
        ? await signIn(trimmedEmail, password, role)
        : await signUp(trimmedEmail, password, role, fullName.trim(), organization.trim());


    setLoading(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    const from = (location.state as { from?: string } | null)?.from;
    navigate(from || successPath, { replace: true });
  };

  // One-click demo login (pilot only).
  const onDemoSignIn = async (acc: DemoAccount) => {
    setError(null);
    setLoading(true);
    setEmail(acc.email);
    setPassword(acc.password);
    const result = await signIn(acc.email, acc.password, role);
    setLoading(false);
    if (result.error) { setError(result.error); return; }
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from || successPath, { replace: true });
  };

  // Filter demo accounts to those matching the portal (role compatibility done server-side too).
  const portalDemoAccounts = (demoAccounts ?? []).filter((a) => {
    const r = a.role.toLowerCase();
    if (role === 'investor') return r === 'investor' || r === 'strategic_partner' || r === 'giisdec_admin' || r === 'admin';
    if (role === 'gis-analyst' || role === 'gis_analyst') return r === 'gis_analyst' || r === 'gis-analyst' || r === 'giisdec_admin' || r === 'admin';
    return true;
  });

  return (
    <section className="min-h-[calc(100vh-4rem)] grid grid-cols-1 md:grid-cols-2 bg-eds-bg font-eds-body">
      {/* ── LEFT · photographic branded panel ─────────────────────────────── */}
      <aside className="relative hidden md:flex flex-col justify-center overflow-hidden px-8 lg:px-12 xl:px-16 py-16 text-white">
        {/* Approved hero photograph + dark-green governed overlay for AA contrast */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/giisdec-exploration.jpg')" }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-eds-forest/85" aria-hidden="true" />
        <div
          className="absolute inset-0 bg-gradient-to-tr from-eds-forest via-eds-forest/70 to-transparent"
          aria-hidden="true"
        />

        <div className="relative z-10 max-w-lg">
          {/* Portal identity */}
          <div className="flex items-center gap-2.5 mb-10">
            <GiisdecMark className="w-9 h-9 flex-shrink-0" />
            <div className="leading-tight">
              <div className="font-eds-display font-bold text-lg tracking-tight text-white">GIISDEC</div>
              <div className="text-[9px] uppercase tracking-widest text-white/60">{panel.identity}</div>
            </div>
          </div>

          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-eds-pill text-xs font-semibold border ${accentChipBg} ${investor ? 'text-eds-mint' : 'text-white'}`}>
            <span className={`w-1.5 h-1.5 rounded-eds-pill ${investor ? 'bg-eds-emerald' : 'bg-eds-gold'}`} />
            {panel.badge}
          </span>

          <h1 className="font-eds-display text-4xl xl:text-5xl font-bold tracking-[-0.02em] mt-6 leading-[1.05]">
            {panel.headLead}<br />
            <span className={investor ? 'text-eds-mint' : 'text-eds-gold'}>{panel.headAccent}</span>
          </h1>

          <p className="text-white/75 mt-5 text-[15px] leading-relaxed max-w-md">{panel.blurb}</p>

          <div className="grid grid-cols-2 gap-3 mt-8 max-w-md">
            {panel.cards.map((c) => (
              <div
                key={c.label}
                className="flex items-center gap-2.5 px-3.5 py-3 rounded-eds-md bg-eds-surface/10 border border-white/15 backdrop-blur-sm"
              >
                <span className={investor ? 'text-eds-mint' : 'text-eds-gold'}>{c.icon}</span>
                <span className="text-sm font-medium text-white/90">{c.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-9 space-y-2 text-sm">
            <Link
              to={otherPortal.to}
              className={`inline-flex items-center gap-1.5 font-semibold ${investor ? 'text-eds-mint' : 'text-eds-gold'} hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded`}
            >
              {investor ? 'GIS analyst? Use the GIS Workspace' : 'Investor? Use the Investor Portal'} <ArrowRight className="w-4 h-4" />
            </Link>
            {/* Government / Agency access — GOVERNED-DISABLED. No dedicated
                government/agency portal route exists in App.tsx and none is
                invented. The label is intentionally non-navigating; the approved
                existing path (/contact) is offered as "Request access". */}
            <div className="pt-1">
              <span
                className="inline-flex items-center gap-1.5 font-medium text-white/65 cursor-default select-none"
                title="Government and Agency Access is available to authorized institutional users. Accounts are provisioned by GIISDEC."
              >
                <Lock className="w-3.5 h-3.5" aria-hidden="true" /> Government / agency access
              </span>
              <p className="text-white/65 text-xs mt-1 leading-relaxed">
                Available to authorized institutional users; accounts are provisioned by GIISDEC.{' '}
                <Link
                  to="/contact"
                  className={`${investor ? 'text-eds-mint' : 'text-eds-gold'} font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded`}
                >
                  Request access
                </Link>.
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* ── RIGHT · sign-in / register card ───────────────────────────────── */}
      <div className="flex items-center justify-center px-4 sm:px-8 py-12">
        <div className="w-full max-w-md">
          {/* Mobile portal identity (left panel is hidden on small screens) */}
          <div className="flex items-center justify-between mb-8">
            <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-eds-pill text-[11px] font-semibold border ${accentChipBg} ${accentText}`}>
              <span className={`w-1.5 h-1.5 rounded-eds-pill ${investor ? 'bg-eds-emerald' : 'bg-eds-gold'}`} />
              {investor ? (mode === 'signup' ? 'Investor Registration' : 'Investor Portal') : 'GIS Workspace'}
            </span>
            <span className="font-eds-display font-bold text-sm tracking-tight text-eds-earth">GIISDEC</span>
          </div>

          {mode === 'signin' ? (
            <>
              <h2 className="font-eds-display text-2xl font-bold text-eds-fg">{title}</h2>
              <p className="text-sm text-eds-muted-fg mt-1.5 mb-7">{subtitle}</p>
            </>
          ) : (
            <>
              <h2 className="font-eds-display text-2xl font-bold text-eds-fg">
                {investor ? 'Create your investor account' : 'Create your GIS account'}
              </h2>
              <p className="text-sm text-eds-muted-fg mt-1.5 mb-6">
                Self-registration is for investors only. Government agencies and GIS users are
                provisioned by GIISDEC.
              </p>
              <div className="flex items-center gap-2 mb-6 px-3 py-2 rounded-eds-md bg-eds-surface-2 border border-eds-border text-sm text-eds-fg">
                <BadgeCheck className={`w-4 h-4 ${accentText}`} aria-hidden="true" />
                Registering as an {investor ? 'Investor' : 'GIS user'}
              </div>
            </>
          )}

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            {mode === 'signup' && (
              <>
                <div>
                  <label htmlFor="lf-fullname" className="text-[13px] font-semibold text-eds-fg block mb-1.5">Full name</label>
                  <div className="relative">
                    <User className="w-4 h-4 absolute left-3 top-3.5 text-eds-muted-fg" />
                    <input
                      id="lf-fullname"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className={inputCls}
                      placeholder="Your name"
                      autoComplete="name"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="lf-org" className="text-[13px] font-semibold text-eds-fg block mb-1.5">Organization</label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 absolute left-3 top-3.5 text-eds-muted-fg" />
                    <input
                      id="lf-org"
                      type="text"
                      value={organization}
                      onChange={(e) => setOrganization(e.target.value)}
                      className={inputCls}
                      placeholder="Company / Agency"
                      autoComplete="organization"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label htmlFor="lf-email" className="text-[13px] font-semibold text-eds-fg block mb-1.5">Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-3.5 text-eds-muted-fg" />
                <input
                  id="lf-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                  placeholder="you@email.com"
                  autoComplete="email"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="lf-password" className="text-[13px] font-semibold text-eds-fg">Password</label>
                {mode === 'signin' && (
                  <Link
                    to="/forgot-password"
                    className={`text-xs font-medium ${accentText} hover:underline focus-visible:outline-none focus-visible:ring-2 ${focusRing} rounded`}
                  >
                    Forgot password?
                  </Link>
                )}
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-3.5 text-eds-muted-fg" />
                <input
                  id="lf-password"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputCls}
                  placeholder={mode === 'signin' ? '••••••••' : 'Create a password'}
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                />
              </div>
            </div>

            {mode === 'signup' && (
              <label htmlFor="lf-consent" className="flex items-start gap-2.5 text-[13px] text-eds-muted-fg cursor-pointer">
                <input
                  id="lf-consent"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-eds-border text-eds-emerald focus:ring-eds-ring/40"
                />
                <span>I consent to identity verification (KYC) and accept the GIISDEC terms of use.</span>
              </label>
            )}

            {error && (
              <div role="alert" className="text-sm text-eds-danger bg-eds-danger/8 border border-eds-danger/30 rounded-eds-md p-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 ${accentBtn} rounded-eds-pill font-semibold transition disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-bg`}
            >
              {mode === 'signin'
                ? <><ShieldCheck className="w-4 h-4" /> {loading ? 'Please wait…' : 'Sign in securely'}</>
                : <>{loading ? 'Please wait…' : 'Create investor account'} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          {mode === 'signin' && (
            <div className="mt-5 pt-5 border-t border-eds-border flex items-center justify-center gap-6 text-xs text-eds-muted-fg">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-eds-emerald-2" /> Hosted at the NITA National Data Centre</span>
              <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-eds-emerald-2" /> Role-Based Access</span>
            </div>
          )}

          {/* Pilot validation account helper — surfaces ONLY when geo-api advertises
              validation accounts AND the build is not production (or the operator
              explicitly sets VITE_AUTH_DEMO_ENABLED=true). Hidden in Pilot
              Production. No credentials are hardcoded here. */}
          {AUTH_DEMO_ENABLED && portalDemoAccounts.length > 0 && (
            <div className="mt-6 pt-6 border-t border-eds-border">
              <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-eds-muted-fg">
                <KeyRound className="w-3.5 h-3.5" aria-hidden="true" />
                Pilot validation accounts
                <span className="ml-auto px-1.5 py-0.5 bg-eds-gold/15 text-eds-earth border border-eds-gold/30 rounded-eds-pill text-[10px]">
                  {AUTH_APP_ENV}
                </span>
              </div>
              <p className="text-xs text-eds-muted-fg mb-3">
                These accounts are served by geo-api for controlled pilot validation only.
              </p>

              <div className="grid gap-2">
                {portalDemoAccounts.map((acc) => (
                  <button
                    key={acc.email}
                    type="button"
                    onClick={() => onDemoSignIn(acc)}
                    disabled={loading}
                    className="w-full text-left px-3 py-2 border border-eds-border rounded-eds-md hover:bg-eds-surface-2 transition disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-bg"
                  >
                    <div className="text-sm font-medium text-eds-fg">{acc.label}</div>
                    <div className="text-xs text-eds-muted-fg font-eds-mono">{acc.email} · {acc.role}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 text-center text-sm text-eds-muted-fg">
            {mode === 'signin' ? (
              <>
                New investor?{' '}
                <button
                  type="button"
                  onClick={() => { setMode('signup'); setError(null); }}
                  className={`${accentText} font-semibold hover:underline`}
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already registered?{' '}
                <button
                  type="button"
                  onClick={() => { setMode('signin'); setError(null); }}
                  className={`${accentText} font-semibold hover:underline`}
                >
                  Sign in
                </button>
              </>
            )}
          </div>

          <div className="mt-4 text-center text-xs text-eds-muted-fg">
            Government agencies and GIS users are provisioned by GIISDEC.{' '}
            <Link to={otherPortal.to} className={`${accentText} font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring rounded`}>
              {otherPortal.label}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoginForm;
